import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/PageHeader';
import ScoreCard from '../../components/ScoreCard';
import TrustStack from '../../components/TrustStack';
import ScoreTrendChart from '../../components/ScoreTrendChart';
import RuleTriggerList from '../../components/RuleTriggerList';
import StatusBadge from '../../components/StatusBadge';
import LoadingSpinner from '../../components/LoadingSpinner';
import Modal from '../../components/Modal';
import { vendorApi } from '../../services/vendor';
import { scoringApi } from '../../services/scoring';
import extractApiError from '../../utils/extractApiError';

export default function VendorGuardDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: () => vendorApi.deleteGuard(id!).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor', 'guards'] });
      queryClient.invalidateQueries({ queryKey: ['vendor', 'sites'] });
      navigate('/vendor/guards');
    },
    onError: (err: unknown) => setDeleteError(extractApiError(err)),
  });

  const { data: guard, isLoading, isError } = useQuery({
    queryKey: ['vendor', 'guards', id],
    queryFn: () => vendorApi.getGuard(id!).then((r) => r.data),
    enabled: !!id,
  });

  const { data: scoreHistory } = useQuery({
    queryKey: ['vendor', 'guard-score-history', id],
    queryFn: () => scoringApi.getVendorScoreHistory('guard', id!),
    enabled: !!id,
  });

  const { data: ruleTriggers } = useQuery({
    queryKey: ['vendor', 'guard-rule-triggers', id],
    queryFn: () => scoringApi.getVendorRuleTriggers('guard', id!),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-red-500">Failed to load guard. Please try again later.</p>
      </div>
    );
  }

  if (!guard) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-slate-500">Guard not found</p>
      </div>
    );
  }

  const snapshots = scoreHistory?.results ?? [];
  const previousScore = snapshots.length >= 2
    ? Number(snapshots[snapshots.length - 2].score_value)
    : null;

  return (
    <>
      <PageHeader
        title={guard.name}
        description={guard.external_id ? `ID: ${guard.external_id}` : undefined}
        actions={
          <div className="flex gap-2">
            <button
              onClick={() => navigate(`/vendor/guards/${id}/edit`)}
              className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-xl hover:bg-brand-700"
            >
              Edit Guard
            </button>
            <button
              onClick={() => { setDeleteError(null); setShowDeleteModal(true); }}
              className="px-4 py-2 text-sm font-medium text-red-600 bg-white border-2 border-red-200 rounded-xl hover:bg-red-50"
            >
              Delete
            </button>
            <button
              onClick={() => navigate('/vendor/guards')}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border-2 border-slate-200 rounded-xl hover:bg-slate-50"
            >
              Back to Guards
            </button>
          </div>
        }
      />

      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={`Delete ${guard.name}?`}
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            This will remove <strong>{guard.name}</strong> from your guards list
            and unassign them from all sites. If this guard has no historical
            reports or scoring data, the record is deleted permanently;
            otherwise it is archived so past reports still resolve to a name.
          </p>
          {deleteError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2">
              <p className="text-sm text-red-700">{deleteError}</p>
            </div>
          )}
          <div className="flex gap-2">
            <button
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-xl hover:bg-red-700 disabled:opacity-50"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Guard'}
            </button>
            <button
              onClick={() => setShowDeleteModal(false)}
              className="px-4 py-2 text-sm text-slate-700 border border-slate-200 rounded-xl hover:bg-slate-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>

      {/* Info + Score row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 rounded-2xl border border-slate-100 bg-white p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Guard Information</h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">First Name</dt>
              <dd className="mt-1 text-sm text-slate-900">{guard.first_name}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">Last Name</dt>
              <dd className="mt-1 text-sm text-slate-900">{guard.last_name}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">Email</dt>
              <dd className="mt-1 text-sm text-slate-900">{guard.email || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">Phone</dt>
              <dd className="mt-1 text-sm text-slate-900">{guard.phone || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">External ID</dt>
              <dd className="mt-1 text-sm text-slate-900">{guard.external_id || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">Status</dt>
              <dd className="mt-1">
                <StatusBadge
                  label={guard.is_active ? 'Active' : 'Inactive'}
                  variant={guard.is_active ? 'success' : 'neutral'}
                />
              </dd>
            </div>
          </dl>
        </div>
        <ScoreCard label="Guard Score" score={guard.score} previousScore={previousScore} size="lg" />
      </div>

      {/* Feature 2: Trust labels (vendor-level data quality picture) */}
      {guard.trust_stack && (
        <div className="mb-8">
          <TrustStack data={guard.trust_stack} variant="full" />
        </div>
      )}

      {/* Score Trend Chart */}
      <div className="rounded-2xl border border-slate-100 bg-white p-6 mb-8">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Score Trend</h2>
        <ScoreTrendChart snapshots={snapshots} />
      </div>

      {/* Rule Triggers */}
      <div className="rounded-2xl border border-slate-100 bg-white p-6 mb-8">
        <RuleTriggerList
          triggers={ruleTriggers?.results ?? []}
          title="Recent Rule Triggers"
        />
      </div>

      {/* Assigned Sites */}
      <div className="rounded-2xl border border-slate-100 bg-white p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">
          Assigned Sites ({guard.assigned_sites?.length ?? 0})
        </h2>
        {guard.assigned_sites && guard.assigned_sites.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Site Name</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Location</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Assigned Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {guard.assigned_sites.map((site) => (
                  <tr
                    key={site.id}
                    className="cursor-pointer hover:bg-blue-50/50"
                    onClick={() => navigate(`/vendor/sites/${site.id}`)}
                  >
                    <td className="px-6 py-4 text-sm text-slate-900 font-medium">{site.name}</td>
                    <td className="px-6 py-4 text-sm text-slate-500">{site.city}, {site.state}</td>
                    <td className="px-6 py-4 text-sm text-slate-500">
                      {new Date(site.assigned_date).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-slate-400">No sites assigned to this guard yet.</p>
        )}
      </div>
    </>
  );
}
